import type { PassMapSide } from "../passMap/helper";

export type HeatmapPoint = {
  x: number; // percentage from 0 to 100
  y: number; // percentage from 0 to 100
  value: number;
  minute: number;
};

export type HeatmapMockData = {
  timeline: {
    minute: number;
    maxMinute: number;
  };
  maxValue: number;
  points: HeatmapPoint[];
};

const centerConcentration: HeatmapPoint[] = [
  { x: 48, y: 48, value: 88, minute: 12 },
  { x: 50, y: 52, value: 94, minute: 25 },
  { x: 52, y: 50, value: 97, minute: 33 },
  { x: 49, y: 55, value: 90, minute: 41 },
  { x: 51, y: 54, value: 92, minute: 55 },
  { x: 50, y: 46, value: 85, minute: 62 },
  { x: 53, y: 47, value: 78, minute: 74 },
];

const midFieldFlow: HeatmapPoint[] = [
  { x: 40, y: 45, value: 60, minute: 10 },
  { x: 60, y: 47, value: 58, minute: 20 },
  { x: 38, y: 55, value: 53, minute: 28 },
  { x: 62, y: 52, value: 57, minute: 34 },
  { x: 41, y: 62, value: 49, minute: 46 },
  { x: 59, y: 61, value: 52, minute: 51 },
];

const wideCoverage: HeatmapPoint[] = [
  { x: 22, y: 48, value: 37, minute: 15 },
  { x: 78, y: 50, value: 34, minute: 27 },
  { x: 30, y: 38, value: 32, minute: 35 },
  { x: 70, y: 42, value: 35, minute: 42 },
  { x: 27, y: 58, value: 30, minute: 56 },
  { x: 73, y: 58, value: 33, minute: 69 },
];

const homeSpread: HeatmapPoint[] = [
  { x: 15, y: 35, value: 22, minute: 5 },
  { x: 14, y: 55, value: 19, minute: 18 },
  { x: 10, y: 45, value: 18, minute: 29 },
  { x: 18, y: 60, value: 20, minute: 44 },
  { x: 13, y: 30, value: 17, minute: 63 },
];

const awaySpread: HeatmapPoint[] = [
  { x: 85, y: 36, value: 24, minute: 7 },
  { x: 82, y: 55, value: 20, minute: 21 },
  { x: 90, y: 45, value: 18, minute: 36 },
  { x: 81, y: 60, value: 21, minute: 48 },
  { x: 88, y: 30, value: 19, minute: 66 },
];

const timeline = {
  minute: 72,
  maxMinute: 90,
};

const centralBurst: HeatmapPoint[] = [
  { x: 50, y: 50, value: 96, minute: 5 },
  { x: 48, y: 46, value: 93, minute: 9 },
  { x: 51, y: 47, value: 91, minute: 14 },
  { x: 47, y: 53, value: 97, minute: 19 },
  { x: 50, y: 52, value: 95, minute: 22 },
  { x: 52, y: 48, value: 92, minute: 30 },
];

const topPlay: HeatmapPoint[] = [
  { x: 45, y: 20, value: 66, minute: 12 },
  { x: 53, y: 18, value: 64, minute: 26 },
  { x: 59, y: 16, value: 70, minute: 34 },
  { x: 48, y: 15, value: 69, minute: 41 },
  { x: 43, y: 14, value: 63, minute: 57 },
];

const bottomPlay: HeatmapPoint[] = [
  { x: 45, y: 80, value: 58, minute: 11 },
  { x: 54, y: 82, value: 60, minute: 23 },
  { x: 52, y: 85, value: 59, minute: 29 },
  { x: 47, y: 88, value: 57, minute: 37 },
  { x: 43, y: 83, value: 61, minute: 49 },
];

const circles: HeatmapPoint[] = [
  { x: 35, y: 45, value: 40, minute: 7 },
  { x: 65, y: 45, value: 42, minute: 18 },
  { x: 32, y: 50, value: 38, minute: 27 },
  { x: 68, y: 50, value: 41, minute: 35 },
  { x: 30, y: 55, value: 39, minute: 44 },
  { x: 70, y: 55, value: 45, minute: 54 },
];

const baseMock: HeatmapPoint[] = [
  ...centerConcentration,
  ...midFieldFlow,
  ...wideCoverage,
  ...centralBurst,
  ...topPlay,
  ...bottomPlay,
  ...circles,
];

export const heatmapMocks: Record<PassMapSide, HeatmapMockData> = {
  home: {
    timeline,
    maxValue: 100,
    points: [
      ...baseMock,
      ...homeSpread,
      { x: 30, y: 35, value: 80, minute: 65 },
      { x: 40, y: 25, value: 72, minute: 68 },
      { x: 55, y: 25, value: 74, minute: 70 },
      { x: 60, y: 35, value: 78, minute: 73 },
      { x: 45, y: 28, value: 76, minute: 77 },
      { x: 15, y: 60, value: 36, minute: 82 },
      { x: 20, y: 30, value: 34, minute: 85 },
    ],
  },
  away: {
    timeline: { ...timeline, minute: 68 },
    maxValue: 100,
    points: [
      ...baseMock,
      ...awaySpread,
      { x: 72, y: 35, value: 84, minute: 63 },
      { x: 75, y: 28, value: 81, minute: 67 },
      { x: 85, y: 32, value: 79, minute: 70 },
      { x: 88, y: 45, value: 77, minute: 72 },
      { x: 70, y: 55, value: 66, minute: 74 },
      { x: 68, y: 70, value: 48, minute: 78 },
      { x: 72, y: 74, value: 44, minute: 88 },
    ],
  },
};
